import React, { useEffect, useMemo, useState } from "react";
import "./settings.css";
import settingsCatalog from "./settingsCatalog.json";
import AuthorizedContent from "../../../shared/AuthorizedContent";
import useAuth from "../../../hooks/useAuth";
import { toast } from "react-toastify";
import axiosInstance from "../../../helpers/axiosInstance";
import { ReactComponent as ArrowIcon } from "../../../assets/signin/right.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/signin/delete.svg";
import { ReactComponent as EditLogo } from "../../../assets/signin/edit.svg";
import FlipLoader from "../../../shared/fliploader/FlipLoader";
import { AnimatePresence, motion } from "framer-motion";
import { ReactComponent as PlusIcon } from "../../../assets/plus.svg";

function formatSettingValue(value) {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  return typeof value === "object" ? JSON.stringify(value) : String(value);
}

function valueToFormField(type, value) {
  if (type === "boolean") {
    return value === true || value === "true" ? "true" : "false";
  }
  if (type === "date") {
    if (!value) return "";
    const parsed = new Date(value);
    return isNaN(parsed) ? String(value) : parsed.toISOString().slice(0, 10);
  }
  if (value === null || value === undefined) {
    return "";
  }
  return typeof value === "object" ? JSON.stringify(value) : String(value);
}

function Settings() {
  const [settingsList, setSettingsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const { organisation, can } = useAuth();
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingSetting, setEditingSetting] = useState(null);
  const [name, setName] = useState("");
  const [label, setLabel] = useState("");
  const [type, setType] = useState("string");
  const [category, setCategory] = useState("");
  const [value, setValue] = useState("");

  const availableCatalogEntries = useMemo(() => {
    const usedLabels = new Set(settingsList.map((setting) => setting.label));
    return settingsCatalog.filter((entry) => !usedLabels.has(entry.label));
  }, [settingsList]);

  useEffect(() => {
    getAllSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAllSettings = () => {
    axiosInstance
      .get(`/api/settings/${organisation._id}/getallsettings`)
      .then((response) => {
        setSettingsList(response.data.data);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        toast("Unable to get settings. Try again!");
        console.log(error);
      });
  };

  const resetForm = () => {
    setName("");
    setLabel("");
    setType("string");
    setCategory("");
    setValue("");
    setEditingSetting(null);
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (setting) => {
    const settingType = setting.type || "string";
    setEditingSetting(setting);
    setName(setting.name);
    setLabel(setting.label);
    setType(settingType);
    setCategory(setting.category || "");
    setValue(valueToFormField(settingType, setting.value));
    setShowModal(true);
  };

  const handleCatalogSelect = (settingName) => {
    const entry = settingsCatalog.find((item) => item.name === settingName);
    if (!entry) {
      setName("");
      setLabel("");
      setType("string");
      setCategory("");
      setValue("");
      return;
    }
    setName(entry.name);
    setLabel(entry.label);
    setType(entry.type);
    setCategory(entry.category);
    setValue(valueToFormField(entry.type, entry.default));
  };

  const upsertSetting = () => {
    if (!name || !label) {
      toast("Please select a setting");
      return;
    }
    const parsedValue = type === "boolean" ? value === "true" : value;
    const data = {
      name,
      label,
      type,
      category,
      value: parsedValue,
      ...(editingSetting ? { _id: editingSetting._id } : {}),
    };
    axiosInstance
      .post(`/api/settings/${organisation._id}/upsertsetting`, data)
      .then((response) => {
        console.log(response.data);
        getAllSettings();
        setShowModal(false);
        resetForm();
        toast(editingSetting ? "Setting updated successfully." : "Setting added successfully.");
      })
      .catch((error) => {
        toast("Unable to save setting. Try again!");
        console.log(error);
      });
  };

  const handleDelete = (id) => {
    axiosInstance
      .delete(`/api/settings/${organisation._id}/deletesetting/${id}`)
      .then((response) => {
        console.log(response.data);
        getAllSettings();
        toast("Setting removed successfully.");
      })
      .catch((error) => {
        toast("Unable to delete setting. Try again!");
        console.log(error);
      });
  };

  // A setting with a fixed list of values, such as the time zone.
  const choices = settingsCatalog.find((entry) => entry.label === label)?.choices;

  const renderModal = () => {
    return (
      <motion.section
        className="settings-modal-container"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        transition={{ duration: 0.2 }}
      >
        <header className="settings-modal-header">
          <ArrowIcon
            className="settings-modal-lefticon"
            onClick={() => {
              setShowModal(false);
              resetForm();
            }}
          />
          {editingSetting ? `Edit Setting${name ? ` – ${name}` : ""}` : "Add Setting"}
        </header>

        <main className="settings-modal-body">
          {!editingSetting &&
            (availableCatalogEntries.length === 0 ? (
              <p>All available settings have already been added for this organisation.</p>
            ) : (
              <div className="settings-form">
                <label className="required">Setting</label>
                <select value={name} onChange={({ target }) => handleCatalogSelect(target.value)} required={true}>
                  <option value="">Select a setting</option>
                  {availableCatalogEntries.map((entry) => (
                    <option key={entry.name} value={entry.name}>
                      {entry.name}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          {(editingSetting || name) && (
            <div className="settings-form">
              <label>Value</label>
              {type === "boolean" ? (
                <select value={value} onChange={({ target }) => setValue(target.value)}>
                  <option value="true">True</option>
                  <option value="false">False</option>
                </select>
              ) : choices ? (
                // Picked, not typed: the server refuses a value it does not know.
                <select value={value} onChange={({ target }) => setValue(target.value)}>
                  <option value="">Select</option>
                  {choices.map((choice) => (
                    <option key={choice} value={choice}>
                      {choice}
                    </option>
                  ))}
                </select>
              ) : type === "date" ? (
                <input type="date" name="value" value={value} onChange={({ target }) => setValue(target.value)} />
              ) : (
                <input type="text" name="value" value={value} onChange={({ target }) => setValue(target.value)} />
              )}
            </div>
          )}
        </main>
        {(editingSetting || name) && (
          <footer className="settings-modal-footer">
            <button className="settings-addbtn" onClick={upsertSetting}>
              Submit
            </button>
          </footer>
        )}
      </motion.section>
    );
  };

  // The fund's logo, as the app sets it: one image, 2MB at most, stored with
  // the members' photos and written into the organisation_logo setting.
  const logo = settingsList.find((setting) => setting.label === "organisation_logo")?.value;
  const uploadLogo = (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast("A logo must be 2MB or smaller.");
      return;
    }
    const form = new FormData();
    form.append("logo", file);
    setUploadingLogo(true);
    axiosInstance
      .post(`/api/settings/${organisation._id}/uploadlogo`, form)
      .then(() => {
        setUploadingLogo(false);
        toast("Logo updated");
        getAllSettings();
      })
      .catch((error) => {
        setUploadingLogo(false);
        toast(error?.response?.data?.message || "Unable to upload the logo. Try again!");
      });
  };

  return (
    <div className="settings-container">
      <AnimatePresence>{showModal ? renderModal() : null}</AnimatePresence>
      <div className="home-header">
        <div className="home-headerleft">
          <h3 className="home-heading">Settings</h3>
        </div>
        <AuthorizedContent feature="settings" action="edit">
          <button className="settings-addiconbtn" onClick={openAddModal}>
            <PlusIcon />
          </button>
        </AuthorizedContent>
      </div>
      <div className="settings-logo">
        {logo ? <img src={logo} alt="Fund logo" /> : <div className="settings-logo-empty">No logo yet</div>}
        <div>
          <p className="settings-listleft-label">Fund logo</p>
          <p className="settings-listleft-name">Shown on the fund's receipts and statements. PNG, JPEG, GIF or SVG, 2MB at most.</p>
          {can("settings", "edit") ? (
            <label className="settings-logo-button">
              {uploadingLogo ? "Uploading…" : logo ? "Replace logo" : "Upload logo"}
              <input
                type="file"
                accept="image/png,image/jpeg,image/gif,image/svg+xml"
                disabled={uploadingLogo}
                onChange={(event) => {
                  uploadLogo(event.target.files[0]);
                  event.target.value = "";
                }}
                hidden
              />
            </label>
          ) : null}
        </div>
      </div>
      <div className="settings-content">
        {loading ? (
          <div className="settings-loader">
            <FlipLoader />
          </div>
        ) : !settingsList?.length ? (
          <p>There are no settings.</p>
        ) : (
          settingsList.map((setting) => (
            <motion.div
              key={setting._id}
              className="settings-list"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div className="settings-listleft">
                <p className="settings-listleft-label">
                  {setting.name} <span className="settings-listleft-type">{setting.type || "string"}</span>
                  {setting.category && <span className="settings-listleft-category">{setting.category}</span>}
                </p>
                <p className="settings-listleft-name">{formatSettingValue(setting.value)}</p>
              </div>

              <AuthorizedContent feature="settings" action="edit">
                <div className="settings-listactions">
                  <div className="settings-listedit" onClick={() => openEditModal(setting)}>
                    <EditLogo />
                  </div>
                  <div className="settings-listrightdelete" onClick={() => handleDelete(setting._id)}>
                    <DeleteIcon />
                  </div>
                </div>
              </AuthorizedContent>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
}

export default Settings;
